import styled, { keyframes } from 'styled-components';

const pulse = keyframes`
  0% {
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4), 0 3px 10px rgba(118, 75, 162, 0.3);
  }
  50% {
    box-shadow: 0 12px 35px rgba(102, 126, 234, 0.6), 0 5px 15px rgba(118, 75, 162, 0.5);
  }
  100% {
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4), 0 3px 10px rgba(118, 75, 162, 0.3);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInOut = keyframes`
  0% {
    opacity: 0;
    transform: translateY(5px);
  }
  20%, 80% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-5px);
  }
`;

export const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f8f9fa;
`;

const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.7);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(82, 196, 26, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(82, 196, 26, 0);
  }
`;

const pulseBeta = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 2px 6px rgba(255, 77, 79, 0.3);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 3px 10px rgba(255, 77, 79, 0.5);
  }
`;

export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  background: transparent;
  
  .header-content {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
  }
  
  .header-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    
    .title-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .status-badge {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #52c41a;
        box-shadow: 0 0 0 2px rgba(82, 196, 26, 0.2);
        animation: ${pulseGlow} 2s infinite;
        position: relative;
        
        &::after {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: #52c41a;
          opacity: 0.6;
          animation: ${pulseGlow} 2s infinite 0.5s;
        }
      }
      
      .beta-badge {
        display: inline-flex;
        align-items: center;
        padding: 3px 10px;
        background: linear-gradient(135deg, #ff7875 0%, #ff4d4f 100%);
        border: 1px solid #ff4d4f;
        border-radius: 12px;
        font-size: 9px;
        font-weight: 700;
        color: #ffffff;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-left: 10px;
        box-shadow: 0 2px 6px rgba(255, 77, 79, 0.3);
        position: relative;
        animation: ${pulseBeta} 2s ease-in-out infinite;
      }
    }
    
    .title {
      margin: 0;
      font-weight: 600;
      color: #262626;
      font-size: 18px;
      letter-spacing: -0.3px;
    }
    
    .subtitle {
      font-size: 12px;
      color: #8c8c8c;
      display: flex;
      align-items: center;
      gap: 6px;
      
      .subtitle-dot {
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: #bfbfbf;
      }
    }
  }
  
  .header-avatar {
    position: relative;
    width: 44px;
    height: 44px;
    background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
    padding: 3px;
    border-radius: 50%;
    box-shadow: 0 4px 12px rgba(82, 196, 26, 0.3);
    transition: all 0.3s ease;
    flex-shrink: 0;
    
    &:hover {
      transform: scale(1.05);
      box-shadow: 0 6px 16px rgba(82, 196, 26, 0.4);
    }
    
    .avatar-inner {
      background: #ffffff;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      padding: 2px;
    }
  }
`;

export const MessageContainer = styled.div`
  display: flex;
  justify-content: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  margin-bottom: 16px;
  
  .ant-space {
    width: 100%;
    max-width: 100%;
  }
`;

export const MessageBubble = styled.div`
  background: ${props => props.isUser ? '#4085c6' : '#ffffff'};
  color: ${props => props.isUser ? '#ffffff' : '#2d3748'} !important;
  padding: 12px 16px;
  border-radius: ${props => props.isUser ? '20px 20px 6px 20px' : '20px 20px 20px 6px'};
  box-shadow: ${props => props.isUser 
    ? '0 4px 12px rgba(24, 144, 255, 0.25)' 
    : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  word-wrap: break-word;
  position: relative;
  border: ${props => !props.isUser ? '1px solid #e5e7eb' : 'none'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  line-height: 1.4;
  
  * {
    color: inherit !important;
  }
  
  &:hover {
    box-shadow: ${props => props.isUser 
      ? '0 6px 16px rgba(64, 133, 198, 0.35)' 
      : '0 4px 12px rgba(0, 0, 0, 0.15)'};
    transform: translateY(-1px);
  }

  .cursor-blink {
    animation: blink 1s infinite;
    color: ${props => props.isUser ? '#ffffff' : '#4085c6'} !important;
  }

  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
`;

export const LoadingPhrase = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #666;
  min-height: 20px;
`;

export const InputContainer = styled.div`
  padding: 20px;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
  
  .ant-space-compact {
    width: 100%;
  }
  
  .ant-input {
    border-radius: 24px;
    border: 1.5px solid #e5e7eb;
    padding: 12px 16px;
    transition: all 0.2s ease;
    background: #ffffff;
    font-size: 14px;
    
    &:focus, &:hover {
      border-color: #1890ff;
      box-shadow: 0 0 0 4px rgba(24, 144, 255, 0.1);
    }
  }
  
  .ant-btn {
    border-radius: 24px;
    height: auto;
    padding: 12px 20px;
    transition: all 0.2s ease;
    font-weight: 500;

    &:active {
      transform: translateY(0);
    }

    &:disabled {
      background: #f5f5f5;
      border-color: #e5e7eb;
      color: #bfbfbf;
      box-shadow: none;
      transform: none;
    }
  }

  /* Enviar. O :not(.ant-btn-dangerous) preserva o vermelho do botão Parar,
     que ocupa a mesma posição enquanto o agente responde. */
  .ant-btn:not(.ant-btn-dangerous) {
    border: 1.5px solid #4085c6;
    background: #4085c6;
    box-shadow: 0 2px 6px rgba(64, 133, 198, 0.25);

    &:hover:not(:disabled) {
      background: #4085c6;
      border-color: #4085c6;
      box-shadow: 0 4px 10px rgba(64, 133, 198, 0.35);
      transform: translateY(-1px);
    }
  }

  .ant-btn-dangerous {
    &:hover:not(:disabled) {
      box-shadow: 0 4px 10px rgba(255, 77, 79, 0.35);
      transform: translateY(-1px);
    }
  }
`;